// src/app/components/reporting/components/report-composer.tsx
import { Sparkles, Send } from 'lucide-react';
import { QuickStartCards } from './quick-start-cards';

interface ReportComposerProps {
  prompt: string; onPromptChange: (v: string) => void;
  onGenerate: () => void; isGenerating: boolean;
  insightCount: number; selectedCount: number;
}

export function ReportComposer({
  prompt, onPromptChange, onGenerate, isGenerating, insightCount, selectedCount,
}: ReportComposerProps) {
  const canGenerate = prompt.trim().length > 0 && !isGenerating;

  return (
    <div
      className="px-4 md:px-6 py-5 md:py-6 rounded-xl"
      style={{
        border: '1px solid var(--border)',
        backgroundColor: 'var(--muted)',
      }}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Sparkles className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--spectral-violet)' }} />
        <h3
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--spectral-violet)',
          }}
        >
          AI Report Composer
        </h3>
      </div>
      <h4
        style={{
          fontSize: 'clamp(var(--text-base), 3.5vw, var(--text-lg))',
          fontWeight: 'var(--font-weight-semibold)',
          marginBottom: '6px',
          lineHeight: '1.3',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        What kind of report do you want to build?
      </h4>
      <p
        style={{
          color: 'var(--muted-foreground)',
          fontSize: 'var(--text-sm)',
          lineHeight: '1.5',
          marginBottom: '16px',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        Describe your goal. AI will compose from your{' '}
        {selectedCount > 0 ? <strong>{selectedCount} selected</strong> : <strong>{insightCount} saved</strong>}{' '}
        insights.
      </p>

      <div className="relative mb-4">
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          rows={3}
          placeholder="e.g., Create a sales deck showing brand perception improvements, targeted at technical buyers..."
          className="w-full p-3 md:p-4 pr-14 rounded-xl resize-none"
          style={{
            backgroundColor: 'var(--background)',
            border: '2px solid var(--border)',
            fontSize: 'var(--text-base)',
            lineHeight: '1.6',
            outline: 'none',
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--alpha-teal)')}
          onBlur={(e)  => (e.currentTarget.style.borderColor = 'var(--border)')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canGenerate) onGenerate();
          }}
        />
        <button
          onClick={onGenerate}
          disabled={!canGenerate}
          className="absolute bottom-3 right-3 w-10 h-10 rounded-lg flex items-center justify-center transition-all"
          style={{
            backgroundColor: canGenerate ? 'var(--alpha-teal)' : 'var(--muted)',
            cursor: canGenerate ? 'pointer' : 'not-allowed',
          }}
          title="Generate report (⌘+Enter)"
        >
          <Send className="w-5 h-5" style={{ color: 'white' }} />
        </button>
      </div>

      <QuickStartCards onSelect={(text) => onPromptChange(text)} />
    </div>
  );
}